// ============================================================
// Tagged discriminated unions
// ============================================================
// A single primitive: `{ tag, data }`. Empty-shape tags carry `data: {}`. The
// rest of this module composes from it.

export type Tagged<K extends string, D> = { tag: K; data: D };

// Shape map `{ K1: D1; K2: D2; ... }` collapsed to a tagged union.
export type TaggedUnion<TShapes> = {
  [K in keyof TShapes & string]: Tagged<K, TShapes[K]>;
}[keyof TShapes & string];

// Strict: an empty-shape tag (`{}`) produces the zero-arg form; a shape
// with keys demands its data (and rejects excess properties).
type TaggedConstructor<K extends string, D> = [keyof D] extends [never]
  ? () => Tagged<K, D>
  : (data: D) => Tagged<K, D>;

export type TaggedConstructors<TShapes> = {
  [K in keyof TShapes & string]: TaggedConstructor<K, TShapes[K]>;
};

// Zero per-tag boilerplate: every property access returns a constructor
// that forwards its own key as `tag`.
//
// Why the `as T` cast is required:
// `new Proxy(target, handler)` is typed as the *target* ({}). The handler
// is what actually implements our interface at runtime — but TS can't
// introspect handler code, so it has no way to verify that every property
// access does what T promises. We assert the shape; the handler's `get`
// does exactly what the types claim (a constructor per string property).
//
// Why `data = {}` (not `undefined`):
// Empty-shape tags are typed as `Tagged<K, {}>` which mandates a non-nullish
// `data`. Defaulting to `{}` keeps the runtime value assignable to that type.
function makeTaggedProxy<T>(): T {
  return new Proxy(
    {},
    {
      get(_target, tag) {
        if (typeof tag !== "string") {
          return undefined;
        }
        return (data: unknown = {}) => ({ tag, data });
      },
    },
  ) as T;
}

export function tagged<TShapes>(): TaggedConstructors<TShapes> {
  return makeTaggedProxy<TaggedConstructors<TShapes>>();
}

// ============================================================
// Screen-flow DSL
// ============================================================
// A screen flow is a map of screens; each declares the data it needs
// and the outcomes its component can emit:
//
//   type Config = {
//     Welcome:    { outcomes: { continue: {} } };
//     BillUpload: { needs: { intent: Intent }; outcomes: { uploaded: { file: File } } };
//   };

export type ScreenKey<TConfig> = keyof TConfig & string;

export type Needs<
  TConfig,
  TScreen extends keyof TConfig,
> = TConfig[TScreen] extends {
  needs: infer N;
}
  ? N
  : // `{}` is the "no needs" sentinel — consumed by
    // `[keyof Needs<…>] extends [never]` checks downstream.
    {};

export type Outcomes<
  TConfig,
  TScreen extends keyof TConfig,
> = TConfig[TScreen] extends { outcomes: infer O } ? O : never;

// The currently-displayed screen plus the data required to render it.
export type FlowState<TConfig> = TaggedUnion<{
  [K in ScreenKey<TConfig>]: Needs<TConfig, K>;
}>;

// The *new* fields the target needs that the source state doesn't already
// carry. Drives the "only supply what's new" transition API — the hook
// accumulates the rest.
type ForwardPatch<
  TConfig,
  TFrom extends keyof TConfig,
  TTo extends keyof TConfig,
> = Omit<Needs<TConfig, TTo>, keyof Needs<TConfig, TFrom>>;

// A forward transition. Empty-needs targets carry `data: {}`; targets with
// declared needs carry the forward patch (the fields the source doesn't
// already have). An empty forward patch (`{}`) is legal — it's the shape
// when the source already satisfies every field of the target.
type GoToAction<TConfig, TFrom extends keyof TConfig> = {
  [TTo in ScreenKey<TConfig>]: [keyof Needs<TConfig, TTo>] extends [never]
    ? { tag: TTo; data: {} }
    : { tag: TTo; data: ForwardPatch<TConfig, TFrom, TTo> };
}[ScreenKey<TConfig>];

// Structurally distinct from forward actions (`type` vs `tag`) so it never
// collides with a screen named "back".
type BackAction = { type: "back" };

export type FlowResult<TConfig, TFrom extends keyof TConfig> =
  | GoToAction<TConfig, TFrom>
  | BackAction;

// Lenient version of tagged constructor for `createFlow`. Shapes with keys
// keep a zero-arg overload because `GoToAction` narrows the target's data
// to an empty forward patch (`{}`) when the source already has every
// field the target needs, and `{ tag; data: D }` is assignable to
// `{ tag; data: {} }` by structural subtyping. The handler's `FlowResult`
// return type is what decides, per call site, whether the zero-arg or
// with-data form is legal.
//
// The data overload takes a `Partial<D>`-shaped literal to support partial
// forward patches (source already carries some fields), intersected with
// a "no extras" guard so excess properties are rejected.

type FlowShapeConstructor<K extends string, D> = [keyof D] extends [never]
  ? () => Tagged<K, {}>
  : {
      (): Tagged<K, D>;
      <TActual extends Partial<D>>(
        data: TActual & { [P in Exclude<keyof TActual, keyof D>]?: never },
      ): Tagged<K, TActual>;
    };

type FlowConstructors<TConfig> = {
  [K in ScreenKey<TConfig>]: FlowShapeConstructor<K, Needs<TConfig, K>>;
};

export function createFlow<TConfig>(): FlowConstructors<TConfig> {
  return makeTaggedProxy<FlowConstructors<TConfig>>();
}

export const back = (): BackAction => ({ type: "back" });

// ============================================================
// Transition table
// ============================================================
// Exhaustive: every screen and every outcome must be handled.

export type TransitionTable<TConfig, TCtx = void> = {
  [K in ScreenKey<TConfig>]: {
    [O in keyof Outcomes<TConfig, K> & string]: (
      output: Outcomes<TConfig, K>[O],
      ctx: TCtx,
    ) => FlowResult<TConfig, K>;
  };
};

// ============================================================
// Component contract
// ============================================================
// Screens call `onDone(outcome)` with a tagged outcome built via
// `tagged<Outcomes>()`. Each screen owns its own outcome shape map and
// types its prop as `OnDone<Outcomes>` — independent of the surrounding
// config. The hook's `bindOnDone(tag)` returns the same shape, so
// assignment type-checks structurally.
export type OnDone<TShapes> = (outcome: TaggedUnion<TShapes>) => void;

export { useBrowserHistorySync } from "./useBrowserHistorySync";
export { type FlowTransition, useNavigableFlow } from "./useNavigableFlow";
